import React, { useEffect, useMemo, useRef, useState } from "react";

/* Lightweight CSS/Canvas-free confetti burst — pure DOM/CSS, no deps. */
const COLORS = ["#FFD700", "#FF007F", "#00F0FF", "#39FF14", "#9D00FF", "#FF9500"];

const CLASS_LABEL = {
  heavyweight: "HEAVYWEIGHT",
  middleweight: "MIDDLEWEIGHT",
  cruiserweight: "CRUISERWEIGHT",
  tag_team: "TAG TEAM",
  wildcard: "WILDCARD",
};

export default function HOFCelebration({ pending, onComplete }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  // Convert pending dict → sorted list of {cls, count}
  const entries = useMemo(() => {
    if (!pending) return [];
    return Object.entries(pending)
      .map(([cls, count]) => ({ cls, count: Number(count) || 0 }))
      .filter((e) => e.count > 0);
  }, [pending]);

  const totalLevels = entries.reduce((a, b) => a + b.count, 0);

  // Build ~120 particles once per show
  const particles = useMemo(() => {
    if (!totalLevels) return [];
    const n = 120;
    return Array.from({ length: n }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.4,
      duration: 2.2 + Math.random() * 1.8,
      size: 6 + Math.random() * 8,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotate: Math.random() * 360,
      drift: (Math.random() - 0.5) * 180,
      shape: Math.random() < 0.5 ? "rect" : "circ",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalLevels]);

  useEffect(() => {
    if (!totalLevels) return;
    setVisible(true);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      onComplete && onComplete();
    }, 3800);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalLevels]);

  if (!visible || !totalLevels) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
      data-testid="hof-celebration"
    >
      {/* Confetti particles */}
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute hof-conf"
          style={{
            left: `${p.left}%`,
            top: "-5%",
            width: `${p.size}px`,
            height: p.shape === "rect" ? `${p.size * 1.6}px` : `${p.size}px`,
            background: p.color,
            borderRadius: p.shape === "circ" ? "50%" : "2px",
            boxShadow: `0 0 8px ${p.color}AA`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            // CSS vars consumed by the keyframe
            "--rot": `${p.rotate}deg`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}

      {/* Big caption */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="hof-caption-pop text-center">
          <div
            className="font-heading uppercase tracking-widest"
            style={{
              fontSize: "clamp(26px, 6vw, 68px)",
              color: "#FFD700",
              textShadow: "0 0 14px #FFD700, 0 0 32px #FF007F, 0 0 48px #FF007F",
              letterSpacing: "0.12em",
            }}
          >
            ⭐ STATUE LEVEL UP! ⭐
          </div>
          <div
            className="mt-3 flex flex-wrap justify-center gap-2"
          >
            {entries.map((e) => (
              <span
                key={e.cls}
                className="px-3 py-1 rounded-full font-heading uppercase text-[11px] tracking-widest"
                style={{
                  background: "#0A051ACC",
                  color: "#00F0FF",
                  border: "1px solid #00F0FF88",
                  boxShadow: "0 0 12px #00F0FF55",
                }}
              >
                {CLASS_LABEL[e.cls] || e.cls} +{e.count}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hof-conf-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          8%   { opacity: 1; }
          100% { transform: translate3d(var(--drift), 110vh, 0) rotate(var(--rot)); opacity: 1; }
        }
        .hof-conf {
          animation-name: hof-conf-fall;
          animation-timing-function: cubic-bezier(.2,.6,.4,1);
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
        @keyframes hof-caption-pop {
          0%   { opacity: 0; transform: scale(0.6); filter: blur(8px); }
          20%  { opacity: 1; transform: scale(1.12); filter: blur(0); }
          40%  { transform: scale(0.98); }
          55%  { transform: scale(1.04); }
          85%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.08); }
        }
        .hof-caption-pop {
          animation: hof-caption-pop 3.8s ease-out forwards;
          will-change: transform, opacity, filter;
        }
      `}</style>
    </div>
  );
}
